import {
	AlertDialog,
	AlertDialogBody,
	AlertDialogContent,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogOverlay,
	Box,
	Button,
	Icon,
	Text,
	useColorModeValue,
} from '@chakra-ui/react';
import { useRef } from 'react';
import { MdWarning, MdError, MdInfo } from 'react-icons/md';
import { headingFont } from 'theme/tokens';

interface ConfirmDialogProps {
	/** Whether the dialog is open */
	isOpen: boolean;
	/** Close callback (cancel) */
	onClose: () => void;
	/** Confirm callback */
	onConfirm: () => void;
	/** Dialog title (default "Are you sure?") */
	title?: string;
	/** Descriptive body text */
	body?: string;
	/** Confirm button label (default "Delete") */
	confirmLabel?: string;
	/** Cancel button label (default "Cancel") */
	cancelLabel?: string;
	/** Shows a spinner on the confirm button */
	isConfirming?: boolean;
	/** Visual intent — controls accent bar, icon, and button color (default "danger") */
	intent?: 'danger' | 'warning' | 'brand';
}

const intentConfig: Record<string, {
	colorScheme: string;
	icon: typeof MdWarning;
	gradient: string;
}> = {
	danger: {
		colorScheme: 'red',
		icon: MdError,
		gradient: 'linear(135deg, danger.500, danger.600)',
	},
	warning: {
		colorScheme: 'orange',
		icon: MdWarning,
		gradient: 'linear(135deg, warning.500, warning.600)',
	},
	brand: {
		colorScheme: 'brand',
		icon: MdInfo,
		gradient: 'linear(135deg, brand.500, brand.400)',
	},
};

export default function ConfirmDialog({
	isOpen,
	onClose,
	onConfirm,
	title = 'Are you sure?',
	body,
	confirmLabel = 'Delete',
	cancelLabel = 'Cancel',
	isConfirming,
	intent = 'danger',
}: ConfirmDialogProps) {
	const cancelRef = useRef<HTMLButtonElement>(null);
	const bodyColor = useColorModeValue('secondaryGray.600', 'gray.400');
	const config = intentConfig[intent];

	return (
		<AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose} isCentered motionPreset='scale'>
			<AlertDialogOverlay bg='blackAlpha.400' backdropFilter='blur(4px)' />
			<AlertDialogContent borderRadius="16px" overflow="hidden">
				{/* Gradient accent bar */}
				<Box h="4px" bgGradient={config.gradient} />

				<AlertDialogHeader
					fontFamily={headingFont}
					fontWeight="700"
					fontSize="lg"
					pt="20px"
					pb="8px"
					display="flex"
					alignItems="center"
					gap="10px"
				>
					<Icon as={config.icon} w="20px" h="20px" color={`${config.colorScheme}.500`} />
					{title}
				</AlertDialogHeader>

				{body && (
					<AlertDialogBody pt={0} pb="16px">
						<Text fontSize="sm" color={bodyColor} lineHeight="1.6">
							{body}
						</Text>
					</AlertDialogBody>
				)}

				<AlertDialogFooter gap={3} pb="20px">
					<Button ref={cancelRef} variant="ghost" onClick={onClose} isDisabled={isConfirming}>
						{cancelLabel}
					</Button>
					<Button
						colorScheme={config.colorScheme}
						isLoading={isConfirming}
						onClick={onConfirm}
					>
						{confirmLabel}
					</Button>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	);
}
